import { ArticleCard, MiniArticleCard } from 'data/global/types';
import {
  BlogHomeQuery_articleCards_items,
  BlogHomeQuery_miniArticleBlocks_items_categoriesCollection_items,
  BlogHomeQuery_articleCards_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/BlogHomeQuery';
import {
  BlogHeroQuery_articleCollection_items,
  BlogHeroQuery_articleCollection_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/BlogHeroQuery';
import { defaultImage } from 'data/DefaultImages/DefaultArticleImage';
import {
  RelatedArticleQuery_articleCollection_items,
  RelatedArticleQuery_articleCollection_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/RelatedArticleQuery';
import {
  AllArticlesPaginatedQuery_articleCollection_items,
  AllArticlesPaginatedQuery_articleCollection_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/AllArticlesPaginatedQuery';
import {
  AllArticlesFromCategoryQuery_articlesByCategory_items,
  AllArticlesFromCategoryQuery_articlesByCategory_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/AllArticlesFromCategoryQuery';
import {
  CategoryMiniArticleById_articleCollection_items,
  CategoryMiniArticleById_articleCollection_items_categoriesCollection_items,
} from 'graphql/__generated__/operations/CategoryMiniArticleById';
import paths from 'constants/paths';
import { SingleArticleQuery_articleCollection_items_categoriesCollection_items } from 'graphql/__generated__/operations/SingleArticleQuery';
import { ParselyMiniArticles_articleCollection_items } from 'graphql/__generated__/operations/ParselyMiniArticles';
import { ParselyArticleCards_articleCollection_items } from 'graphql/__generated__/operations/ParselyArticleCards';

export function articleCardTransformer(
  article:
    | BlogHeroQuery_articleCollection_items
    | BlogHomeQuery_articleCards_items
    | AllArticlesPaginatedQuery_articleCollection_items
    | AllArticlesFromCategoryQuery_articlesByCategory_items
    | ParselyArticleCards_articleCollection_items
    | null,
): ArticleCard {
  const { categoryAssemblyHref, categoryAssemblyTitle } = categoryFromAssembly(
    article?.categoriesCollection?.items,
  );

  return {
    topicId: article?.sys.id ?? '',
    author: {
      name: article?.authorsCollection?.items[0]?.displayName ?? '',
      email: article?.authorsCollection?.items[0]?.email ?? '',
    },
    categoryAssemblyTitle,
    categoryAssemblyHref,
    description: article?.shortDescription ?? '',
    slug: article?.slug ?? '',
    editorialLastPublishedDateComputed:
      article?.editorialLastPublishedDateComputed ?? '',
    editorialOriginalPublishDate: article?.editorialOriginalPublishDate ?? '',
    timeToRead: article?.readTimeComputed ?? 0,
    title: article?.title ?? '',
    image: {
      alt: article?.primaryImage?.describedImage ?? '',
      url: article?.primaryImage?.image?.url ?? defaultImage(),
      title: article?.primaryImage?.image?.title ?? '',
      maxWidth: article?.primaryImage?.maxWidth ?? null,
    },
  };
}

export function miniArticleCardTransformer(
  article:
    | RelatedArticleQuery_articleCollection_items
    | CategoryMiniArticleById_articleCollection_items
    | ParselyMiniArticles_articleCollection_items
    | null,
): MiniArticleCard {
  const { categoryAssemblyHref, categoryAssemblyTitle } = categoryFromAssembly(
    article?.categoriesCollection?.items,
  );

  return {
    topicId: article?.sys.id ?? '',
    title: article?.title ?? '',
    categoryAssemblyTitle,
    categoryAssemblyHref,
    image: {
      url: article?.primaryImage?.image?.url ?? defaultImage(),
      alt: article?.primaryImage?.describedImage ?? '',
      title: article?.primaryImage?.image?.title ?? '',
      maxWidth: article?.primaryImage?.maxWidth ?? null,
    },
    slug: article?.slug ?? '',
  };
}

export function categoryFromAssembly(
  categories: Array<
    | BlogHomeQuery_miniArticleBlocks_items_categoriesCollection_items
    | RelatedArticleQuery_articleCollection_items_categoriesCollection_items
    | CategoryMiniArticleById_articleCollection_items_categoriesCollection_items
    | BlogHeroQuery_articleCollection_items_categoriesCollection_items
    | BlogHomeQuery_articleCards_items_categoriesCollection_items
    | AllArticlesPaginatedQuery_articleCollection_items_categoriesCollection_items
    | AllArticlesFromCategoryQuery_articlesByCategory_items_categoriesCollection_items
    | SingleArticleQuery_articleCollection_items_categoriesCollection_items
    | null
  > = [],
): { categoryAssemblyTitle: string; categoryAssemblyHref: string } {
  const categoryAssembly = categories.find(
    (category) => category?.parentCategory !== null,
  )?.linkedFrom?.categoryAssemblyCollection?.items[0];

  if (
    !categoryAssembly ||
    !categoryAssembly?.slug ||
    !categoryAssembly.category?.title
  ) {
    // @TODO Log this to Sentry
    // This error should break the build as it's not possible that an article does not belong
    // to a child category.
    // More info here: https://zapier.slack.com/archives/CTYSRSE4A/p1605556947236400

    throw new Error('We found an article without a child category');
  }

  return {
    categoryAssemblyHref: paths.categoryPage({
      categoryAssemblySlug: categoryAssembly.slug,
    }),
    categoryAssemblyTitle: categoryAssembly.category?.title,
  };
}
